import { AxiosError, AxiosInstance, AxiosRequestConfig } from "axios";

let isRefreshing = false;
let failedQueue: {
  resolve: (token: string | null) => void;
  reject: (reason?: unknown) => void;
}[] = [];

function processQueue(error: unknown, token: string | null = null) {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
      return;
    }

    resolve(token);
  });

  failedQueue = [];
}

type RetryableRequestConfig = AxiosRequestConfig & {
  retryAfterRefresh?: boolean;
};

function setRequestAuthHeader(config: AxiosRequestConfig, token: string) {
  const bearerHeader = `Bearer ${token}`;
  const headers = (config.headers || {}) as AxiosRequestConfig["headers"] & {
    common?: Record<string, string>;
    authorization?: string;
  };

  if (headers.common) {
    headers.common.authorization = bearerHeader;
  } else {
    headers.authorization = bearerHeader;
  }

  config.headers = headers;
}

export function setupInterceptors(
  apiInstance: AxiosInstance,
  onRefreshToken: () => Promise<string | null>,
  onSignOut: () => void,
): () => void {
  const requestInterceptorId = apiInstance.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem("@ctech:token");

      if (token) {
        setRequestAuthHeader(config, token);
      }

      return config;
    },
  );

  const responseInterceptorId = apiInstance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as
        | RetryableRequestConfig
        | undefined;

      if (error.response?.status !== 401 || !originalRequest) {
        return Promise.reject(error);
      }

      if (originalRequest.retryAfterRefresh) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise<string | null>((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          if (token) {
            setRequestAuthHeader(originalRequest, token);
          }

          originalRequest.retryAfterRefresh = true;
          return apiInstance(originalRequest);
        });
      }

      originalRequest.retryAfterRefresh = true;
      isRefreshing = true;

      try {
        const newToken = await onRefreshToken();

        if (!newToken) {
          processQueue(error, null);
          onSignOut();
          throw error;
        }

        processQueue(null, newToken);
        setRequestAuthHeader(originalRequest, newToken);
        return await apiInstance(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        throw refreshError;
      } finally {
        isRefreshing = false;
      }
    },
  );

  return () => {
    apiInstance.interceptors.request.eject(requestInterceptorId);
    apiInstance.interceptors.response.eject(responseInterceptorId);
  };
}
